import { Column } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

export type CampaignObjective =
	| 'cold_acquisition'
	| 'nurture'
	| 'reengagement'
	| 'ar_collection'
	| 'announcement';

export type CampaignStatus =
	| 'draft'
	| 'active'
	| 'paused'
	| 'completed'
	| 'archived';

/**
 * A campaign is a Workd-level outreach initiative / offer (e.g. "Workd ID push").
 *
 * In Instantly it fans out to many provider campaigns (each essentially a
 * sequence) — see CampaignProviderCampaignEntity — with a given contact enrolled
 * in exactly one of them. Workd is the system of record for the campaign;
 * Instantly remains the sending + deliverability engine. `provider` plus the
 * external ids on the child tables form the provider-adapter seam so the sending
 * provider can be swapped without remodeling.
 *
 * The record owner is the inherited `ownerUserId`.
 */
@Entity('campaign', {}, { auditable: true })
export class CampaignEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	// App-validated (CampaignObjective). Kept as varchar so the offer taxonomy can
	// grow without an enum-altering migration.
	@Column('varchar', { name: 'objective', length: 50, nullable: true })
	public objective!: CampaignObjective | string | null;

	@Column('varchar', { name: 'status', length: 50, default: () => "'draft'" })
	public status!: CampaignStatus | string;

	@Column('varchar', {
		name: 'provider',
		length: 50,
		default: () => "'instantly'",
	})
	public provider!: string;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('datetime', { name: 'startDate', nullable: true })
	public startDate!: Date | null;

	@Column('datetime', { name: 'endDate', nullable: true })
	public endDate!: Date | null;

	@Column('json', { name: 'settings', nullable: true })
	public settings!: Record<string, unknown> | null;
}
